import * as React from "react";
import { SkeletonText, Text, Tooltip } from "src/components";
import { FormControl, useTranslation } from "src/hooks";
import { shortID } from "src/helpers";
import styled, { css } from "styled-components";
import { randomNumberBetween } from "src/helpers/numbers";
import { ChangeEvent, useEffect, useState } from "react";
import { Info } from "@asphalt-react/iconpack";

export interface IInputProps extends React.HTMLProps<HTMLInputElement> {
  innerRef?: React.Ref<any>;
  helperText?: string;
  error?: boolean;
  isLoading?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  formControl?: FormControl;
  qualifier?: React.ReactNode;
  outline?: boolean;
  tooltipLabel?: string;
  // For testing UI
  staticSkeletonWidth?: number;
  required?: boolean;
  reverseQualifier?: boolean;
}

export const InputContainerEl = styled.div`
  display: flex;
  position: relative;
  flex-direction: column;
  margin-bottom: 16px;
`;

export const FieldContainer = styled.div`
  display: flex;
  gap: 0.25rem;
`;

export const InputLabel = styled.label<IInputProps>`
  color: rgb(27, 40, 66);
  display: block;
  font-family: ${(props) => props.theme.text.fontFamily.demi};
  font-size: ${(props) =>
    props.theme.text.size[props.theme.components.input.labelFontSize]};
  margin-bottom: 8px;

  ${(props) =>
    props.readOnly &&
    !props.outline &&
    css`
      margin-bottom: 0;
    `};
`;

const InputWrapperEl = styled.div<IInputProps>`
  border-radius: ${(p) => p.theme.dimension.borderRadius[0]}px;
  border: 1px solid ${(props) => props.theme.colors.borders.active};
  display: flex;
  height: 40px;

  ${(props) =>
    props.error &&
    css`
      border-color: ${props.theme.colors.background.danger};
    `};

  ${(props) =>
    props.readOnly &&
    !props.outline &&
    css`
      border: none;
      height: 32px;
    `};

  ${(props) =>
    props.disabled &&
    css`
      background-color: ${props.theme.colors.background.disabled};
      border-color: ${props.theme.colors.background.disabled};
    `};

  ${(props) =>
    props.reverseQualifier &&
    css`
      flex-direction: row-reverse;
    `}
`;

const QualifierWrapperEl = styled.div<Partial<IInputProps>>`
  align-items: center;
  display: flex;
  flex-shrink: 0;
  justify-content: center;
  min-width: 36px;
  padding: 0 8px;

  ${(p) =>
    p.readOnly &&
    css`
      justify-content: flex-start;
      min-width: 20px;
    `}
`;

const InputEl = styled.input<IInputProps>`
  background: transparent;
  border: none;
  font-family: ${(props) => props.theme.text.fontFamily.normal};
  font-size: ${(props) => props.theme.text.size.normal};
  height: 100%;
  outline: none;
  padding: 0;
  text-indent: 12px;
  width: 100%;

  ${(props) =>
    props.error &&
    css`
      color: ${props.theme.colors.text.danger};
    `};

  ${(props) =>
    props.readOnly &&
    css`
      color: rgb(36, 54, 88);
      font-family: ${(props) => props.theme.text.fontFamily.book};
    `};

  ${(props) =>
    (props.qualifier || (props.readOnly && !props.outline)) &&
    css`
      text-indent: 0;
    `};

  ${(props) =>
    props.disabled &&
    css`
      color: ${props.theme.colors.text.disabled};
      cursor: not-allowed;
    `};

  ${(props) => css`
    ::placeholder,
    ::-webkit-input-placeholder {
      color: ${props.theme.colors.text.placeholder};
    }
    :-ms-input-placeholder {
      color: ${props.theme.colors.text.placeholder};
    }
  `}

  ${(props) =>
    props.reverseQualifier &&
    css`
      padding: 0 ${props.theme.dimension.spacing.m}px;
    `}
`;

export const Input = ({
  innerRef,
  label,
  id = shortID(),
  name = id,
  helperText,
  error,
  readOnly,
  disabled,
  isLoading,
  formControl,
  qualifier,
  staticSkeletonWidth = randomNumberBetween(30, 95),
  required,
  onChange,
  onBlur,
  outline,
  pattern,
  reverseQualifier,
  tooltipLabel,
  ...other
}: IInputProps) => {
  // random skeleton text width for a more dynamic feel
  const [skeletonWidth] = useState(`${staticSkeletonWidth}%`);
  const [_error, setError] = useState(formControl?.errors || error);
  const [_helperText, setHelperText] = useState(
    formControl?.errors?.message || helperText
  );

  const [inputValue, setInputValue] = useState(other.defaultValue || "");
  const [blur, setBlur] = useState(false);

  const _name = formControl?.name || name;
  const _Ref = formControl?.register || innerRef;

  const elementProps: any = {
    disabled,
    error: _error,
    readOnly,
    outline,
    reverseQualifier,
  };

  const genericTranslate = useTranslation();

  const _onChange = (event: ChangeEvent<HTMLInputElement>) => {
    setBlur(false);
    const matchPattern = String(event.target.value).match(pattern!);
    if (pattern && !matchPattern) {
      return;
    }
    setInputValue(event?.target?.value);
    onChange?.(event);
  };

  const _onBlur = (event: any) => {
    setBlur(true);
    onBlur?.(event);
  };

  useEffect(() => {
    setError(formControl?.errors || error);
    setHelperText(formControl?.errors?.message || helperText);
    if (required) {
      if (blur && inputValue.toString().length === 0) {
        setError(true);
        setHelperText(genericTranslate("generic.input.error.required"));
      } else {
        setError(error);
        setHelperText(helperText);
      }
    }
  }, [
    required,
    inputValue,
    blur,
    genericTranslate,
    formControl,
    error,
    helperText,
  ]);

  return (
    <InputContainerEl>
      {label && (
        <InputLabel {...elementProps} htmlFor={id}>
          {tooltipLabel ? (
            <FieldContainer>
              {label}
              <Tooltip label={tooltipLabel} placement={"right"}>
                <Info />
              </Tooltip>
            </FieldContainer>
          ) : (
            label
          )}
        </InputLabel>
      )}
      {/* show loading skeleton on readOnly inputs only */}
      {isLoading && readOnly && (
        <SkeletonText
          marginTopS
          style={{ width: skeletonWidth, marginBottom: "6px" }}
        >
          <Text fontSize="medium">12345</Text>
        </SkeletonText>
      )}
      {!isLoading && (
        <InputWrapperEl {...elementProps} error={_error}>
          {qualifier && (
            <QualifierWrapperEl readOnly={readOnly} outline={outline}>
              {qualifier}
            </QualifierWrapperEl>
          )}
          <InputEl
            {...elementProps}
            {...other}
            id={id}
            ref={_Ref}
            name={_name}
            qualifier={qualifier}
            onChange={_onChange}
            onBlur={_onBlur}
            pattern={pattern}
          />
        </InputWrapperEl>
      )}
      {_helperText && (
        <Text
          marginTopS
          fontSize="small"
          color={_error ? "danger" : "normal"}
          fontWeight="book"
          {...elementProps}
        >
          {_helperText}
        </Text>
      )}
    </InputContainerEl>
  );
};

export const Password = (props: IInputProps) => (
  <Input type="password" label="Password" placeholder="●●●●●●●●" {...props} />
);

export const OTPInput = (props: IInputProps) => (
  <Input type="number" label="OTP" placeholder="● ● ● ●" {...props} />
);
